import React from 'react';
import clsx from 'clsx';
import { ControlPlanItem } from '../../types';

interface ControlPlanTableProps {
    items: ControlPlanItem[];
    isDark: boolean;
    selectedItems: Set<string>;
    toggleSelection: (id: string) => void;
    setSelectedItems: (items: Set<string>) => void;
    handleUpdateItem: (id: string, field: keyof ControlPlanItem, value: string) => void;
}

const ControlPlanTable: React.FC<ControlPlanTableProps> = ({
    items,
    isDark,
    selectedItems,
    toggleSelection,
    setSelectedItems,
    handleUpdateItem,
}) => {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className={clsx(isDark ? 'bg-slate-800' : 'bg-slate-50')}>
                    <tr>
                        <th className="px-3 py-3 text-left w-10">
                            <input
                                type="checkbox"
                                checked={selectedItems.size === items.length && items.length > 0}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedItems(new Set(items.map(i => i.id)));
                                    } else {
                                        setSelectedItems(new Set());
                                    }
                                }}
                                className="rounded"
                            />
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Step #</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Process Step</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Machine</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Characteristic</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300 w-12">CC/SC</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Spec</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Eval Method</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Sample</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Control</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Reaction Plan</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {items.map(item => (
                        <tr key={item.id} className={clsx(
                            'transition-colors',
                            isDark ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-slate-50',
                            selectedItems.has(item.id) && 'bg-blue-50 dark:bg-blue-900/20'
                        )}>
                            <td className="px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={selectedItems.has(item.id)}
                                    onChange={() => toggleSelection(item.id)}
                                    className="rounded"
                                />
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{item.processStepNumber}</td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.processStepName}
                                    onChange={(e) => handleUpdateItem(item.id, 'processStepName', e.target.value)}
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.machine || ''}
                                    onChange={(e) => handleUpdateItem(item.id, 'machine', e.target.value)}
                                    placeholder="Machine"
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.characteristic}
                                    onChange={(e) => handleUpdateItem(item.id, 'characteristic', e.target.value)}
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2 text-center">
                                <select
                                    value={item.specialCharacteristic}
                                    onChange={(e) => handleUpdateItem(item.id, 'specialCharacteristic', e.target.value)}
                                    className={clsx(
                                        'px-2 py-1 rounded border text-xs font-bold',
                                        item.specialCharacteristic === 'CC' ? 'bg-red-100 text-red-700 border-red-300' :
                                            item.specialCharacteristic === 'SC' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                                isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
                                    )}
                                >
                                    <option value="none">—</option>
                                    <option value="CC">CC</option>
                                    <option value="SC">SC</option>
                                </select>
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.specification}
                                    onChange={(e) => handleUpdateItem(item.id, 'specification', e.target.value)}
                                    placeholder="Spec/Tol"
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.evaluationTechnique}
                                    onChange={(e) => handleUpdateItem(item.id, 'evaluationTechnique', e.target.value)}
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">
                                {item.sampleSize} / {item.sampleFrequency}
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.controlMethod}
                                    onChange={(e) => handleUpdateItem(item.id, 'controlMethod', e.target.value)}
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="text"
                                    value={item.reactionPlan}
                                    onChange={(e) => handleUpdateItem(item.id, 'reactionPlan', e.target.value)}
                                    className={clsx('w-full px-2 py-1 rounded border text-sm', isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200')}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ControlPlanTable;
